/**
 * MCC Viewer Modal (FOH, read-only)
 * =================================
 * Surfaces every MCC entry the tester captured for this job in one
 * scrollable drawer. Opened from the "View MCC" button at the top of
 * the FOH Pricing screen.
 *
 * Read-only — FOH never edits the tester's evidence directly. Each
 * card shows:
 *   • The defect text (verbatim DVSA wording)
 *   • Severity badge (Light / Moderate / Heavy)
 *   • Status (pending / complete / skipped / escalated)
 *   • Technician note + rapid-capture fields (size, brand, mileage…)
 *   • All photos (click to enlarge in a new tab)
 *
 * Hidden when:
 *   • the modal `open` prop is false
 *   • OR `jobId` is missing
 *   • OR the job has zero MCC entries (caller-side gating is
 *     responsible for not opening it in that case, but we still
 *     render an empty state defensively).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { X, Camera, Stethoscope, AlertTriangle, CheckCircle2, ClipboardList, ExternalLink } from "lucide-react";

const SEV_STYLES = {
  Light:     "bg-emerald-100 text-emerald-800 border-emerald-300",
  Moderate:  "bg-amber-100 text-amber-800 border-amber-300",
  Heavy:     "bg-red-100 text-red-800 border-red-300",
};

const STATUS_STYLES = {
  pending:   { label: "Pending",   cls: "bg-slate-200 text-slate-700" },
  skipped:   { label: "Skipped",   cls: "bg-slate-300 text-slate-600" },
  complete:  { label: "Complete",  cls: "bg-emerald-200 text-emerald-800" },
  escalated: { label: "Escalated", cls: "bg-purple-200 text-purple-800" },
};

function _severityLabel(raw) {
  const s = (raw || "").toLowerCase();
  if (s === "heavy" || s === "dangerous" || s === "major") return "Heavy";
  if (s === "moderate" || s === "minor") return "Moderate";
  if (s === "light" || s === "advisory") return "Light";
  return raw || "—";
}

export default function MCCViewerModal({ open, onClose, jobId, jobReg }) {
  const [entries, setEntries] = useState([]);
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!open || !jobId) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/jobs/${jobId}/mcc`)
      .then((r) => {
        setEntries(r.data?.mcc_entries || []);
        setDefects(r.data?.defects || []);
        setTestResult(r.data?.test_result || null);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load MCC evidence"))
      .finally(() => setLoading(false));
  }, [open, jobId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="mcc-viewer-overlay"
    >
      <div
        className="bg-white w-full max-w-2xl h-full shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="mcc-viewer-drawer"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-teal-600 to-teal-700 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Stethoscope className="w-5 h-5" />
            <div>
              <h2 className="font-bold text-base leading-tight">MCC — Tester Evidence</h2>
              <p className="text-[11px] opacity-90 leading-tight mt-0.5">
                {jobReg || "—"}
                {testResult && (
                  <span className={`ml-2 font-bold ${testResult === "FAILED" ? "text-red-200" : "text-emerald-200"}`}>
                    · MOT {testResult}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-white/10 transition-colors"
            data-testid="mcc-viewer-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && (
            <div className="text-center text-sm text-slate-500 py-12" data-testid="mcc-viewer-loading">
              Loading tester evidence…
            </div>
          )}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm" data-testid="mcc-viewer-error">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              {error}
            </div>
          )}
          {!loading && !error && entries.length === 0 && defects.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-12" data-testid="mcc-viewer-empty">
              <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No MCC evidence captured for this job.
            </div>
          )}

          {/* Pending defects without entries (rare, but possible when
              the tester only ran the MOT and the entries weren't built
              yet). Surface them so FOH still sees the defect list. */}
          {!loading && !error && entries.length === 0 && defects.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                MOT Defects ({defects.length})
              </p>
              {defects.map((d, i) => (
                <div
                  key={`def-${i}`}
                  className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                  data-testid={`mcc-viewer-defect-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-medium uppercase ${SEV_STYLES[_severityLabel(d.severity)] || "bg-slate-100 text-slate-700"}`}>
                      {_severityLabel(d.severity)}
                    </span>
                    <p className="text-sm text-slate-800 leading-snug flex-1">{d.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {entries.map((e, idx) => {
            const sevLabel = _severityLabel(e.severity_choice || e.severity);
            const statusStyle = STATUS_STYLES[(e.status || "pending").toLowerCase()] || STATUS_STYLES.pending;
            const rcFields = (e.rapid_capture && e.rapid_capture.fields) || {};
            return (
              <div
                key={e.id || `mcc-${idx}`}
                className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
                data-testid={`mcc-viewer-entry-${idx}`}
              >
                {/* Top row: severity + status */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded border font-medium uppercase ${SEV_STYLES[sevLabel] || "bg-slate-100 text-slate-700"}`}>
                    {sevLabel}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${statusStyle.cls}`}>
                    {statusStyle.label}
                  </span>
                  {e.card_type && (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                      {String(e.card_type).replace(/_/g, " ")}
                    </span>
                  )}
                </div>

                {/* Defect text */}
                <p className="text-sm font-medium text-slate-800 leading-snug mb-2">
                  {e.defect_text || "(no description)"}
                </p>

                {/* Rapid-capture fields */}
                {Object.keys(rcFields).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(rcFields).map(([k, v]) => {
                      if (v == null || v === "") return null;
                      // iter211ad — tester_notes is a full-form
                      // paragraph; rendered separately below.
                      if (k === "tester_notes") return null;
                      return (
                        <span
                          key={k}
                          className="text-[10.5px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded border border-slate-200"
                        >
                          <b className="text-slate-500 mr-1">{k.replace(/_/g, " ")}:</b>{String(v)}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* iter211ad — Tester free-text notes (from
                    rapid_capture.fields.tester_notes) as a full-width
                    call-out so FOH can spot outlying cases. */}
                {(rcFields.tester_notes || "").trim() && (
                  <div
                    className="mt-2 text-[12.5px] text-amber-900 bg-amber-50 border-l-2 border-amber-400 px-3 py-2 rounded-r leading-relaxed whitespace-pre-wrap"
                    data-testid={`mcc-viewer-tester-notes-${idx}`}
                  >
                    <span className="font-semibold text-amber-800">Notes from tester:</span> {rcFields.tester_notes}
                  </div>
                )}

                {/* Technician note */}
                {(e.technician_note || "").trim() && (
                  <div className="mt-2 text-[12.5px] text-slate-700 bg-amber-50/40 border-l-2 border-amber-400 px-3 py-2 rounded-r leading-relaxed">
                    <span className="font-semibold text-amber-800">Tester note:</span> {e.technician_note}
                  </div>
                )}

                {/* Recommendation */}
                {e.recommendation && (
                  <div className="mt-2 flex items-center gap-1.5 text-[12px] text-emerald-800">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span className="font-medium">Recommendation:</span>
                    <span>{e.recommendation}</span>
                  </div>
                )}

                {/* Photos */}
                {Array.isArray(e.photos) && e.photos.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                      <Camera className="w-3 h-3" />
                      Photos ({e.photos.length})
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {e.photos.map((p, pi) => (
                        <a
                          key={`ph-${pi}`}
                          href={p.url || p}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block aspect-video bg-slate-100 rounded-md overflow-hidden border border-slate-200 hover:ring-2 hover:ring-teal-400 transition-all relative group"
                          data-testid={`mcc-viewer-photo-${idx}-${pi}`}
                        >
                          <img
                            src={p.url || p}
                            alt={p.label || `Tester evidence ${pi + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <ExternalLink className="w-3 h-3 absolute top-1 right-1 text-white opacity-0 group-hover:opacity-100 drop-shadow" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 text-center shrink-0">
          Read-only — tester evidence cannot be edited from the FOH pricing screen.
        </div>
      </div>
    </div>
  );
}
